/**
 * WelcomeTasksPage - Step 4 of App Orientation Flow
 * 
 * Practical hands-on tasks with real data to build confidence.
 * Users complete actual tasks in the application to practice key workflows.
 */

import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useConnection } from '../../contexts/EnhancedConnectionContext';
import { userAPI } from '../../api/enhancedApiService';
import { 
  CheckCircleIcon,
  ClockIcon,
  PlayIcon,
  ArrowRightIcon,
  ArrowLeftIcon,
  EyeIcon,
  MagnifyingGlassIcon,
  ExclamationTriangleIcon,
  ChartBarIcon
} from '@heroicons/react/24/outline';
import Logo from '../../components/common/Logo';

const WelcomeTasksPage = () => {
  const navigate = useNavigate();
  const { connections } = useConnection();
  const [completedTasks, setCompletedTasks] = useState(new Set());

  // Get first connection for task examples
  const primaryConnection = connections?.[0];
  
  // Get real table name for tasks
  const sampleTableName = 'customers'; // Default fallback

  const tasks = [
    {
      id: 'explore-table',
      title: 'Explore a Data Table',
      description: 'Navigate to the Data Explorer and examine the structure of a key table',
      estimated: '2 min',
      difficulty: 'Easy',
      steps: [
        'Go to Data Explorer from the main navigation',
        `Select the "${primaryConnection?.name || 'database'}" connection`,
        `Browse to find a table like "${sampleTableName}" or any available table`,
        'Click on the table to view its details',
        'Review the columns, data types, and sample data'
      ],
      actionLink: primaryConnection ? `/explorer/${primaryConnection.id}` : '/explorer',
      actionText: 'Open Data Explorer',
      icon: MagnifyingGlassIcon,
      category: 'Data Exploration'
    },
    {
      id: 'check-validations',
      title: 'Review Validation Results',
      description: 'Check the current status of data quality validations',
      estimated: '3 min',
      difficulty: 'Easy',
      steps: [
        'Navigate to the Validations page',
        'Select a table with existing validations (or any available table)',
        'Review the validation rules and their results',
        'Look at recent execution history',
        'Identify any failed validations or create your first rule'
      ],
      actionLink: '/validations',
      actionText: 'View Validations',
      icon: CheckCircleIcon,
      category: 'Quality Monitoring'
    },
    {
      id: 'dashboard-overview',
      title: 'Navigate the Dashboard',
      description: 'Get familiar with the main dashboard and key metrics',
      estimated: '2 min',
      difficulty: 'Easy',
      steps: [
        'Return to the main Dashboard',
        `Review the health summary for "${primaryConnection?.name || 'your connection'}"`,
        'Check recent alerts and notifications',
        'Explore different metric cards and trends',
        'Try switching between different connections if available'
      ],
      actionLink: '/dashboard',
      actionText: 'Go to Dashboard',
      icon: ChartBarIcon,
      category: 'Navigation'
    },
    {
      id: 'create-issue',
      title: 'Create a Data Quality Issue',
      description: 'Practice creating an issue to track a data quality problem',
      estimated: '4 min',
      difficulty: 'Medium',
      steps: [
        'Go to the Issues page',
        'Click "Create New Issue"',
        `Fill in a sample issue (e.g., "Data quality concern in ${sampleTableName} table")`,
        'Select appropriate priority and category',
        'Add a description with context about what you observed',
        'Save the issue'
      ],
      actionLink: '/issues',
      actionText: 'Manage Issues',
      icon: ExclamationTriangleIcon,
      category: 'Issue Management'
    }
  ];

  const handleTaskComplete = (taskId) => {
    setCompletedTasks(prev => new Set([...prev, taskId]));
  };

  const handleContinue = async () => {
    try {
      // Update progress in backend
      await userAPI.updateOnboardingProgress({
        current_step: 'tasks',
        steps_completed: ['welcome', 'data', 'features', 'tasks'],
        completion_rate: 80, // 4 of 5 steps
        tasks_completed: {
          completed_task_ids: Array.from(completedTasks),
          total_tasks: tasks.length,
          task_completion_rate: (completedTasks.size / tasks.length) * 100,
          completed_at: new Date().toISOString()
        }
      });
      
      console.log('[WelcomeTasks] Updated onboarding progress');
    } catch (error) {
      console.error('[WelcomeTasks] Error tracking progress:', error);
      // Continue with navigation even if tracking fails
    }
    
    navigate('/welcome/complete');
  };

  const handleBack = () => {
    navigate('/welcome/features');
  };

  const getDifficultyColor = (difficulty) => {
    switch (difficulty) {
      case 'Easy': return 'bg-green-100 text-green-800';
      case 'Medium': return 'bg-yellow-100 text-yellow-800';
      case 'Hard': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const completionRate = (completedTasks.size / tasks.length) * 100;

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-6xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Logo size="medium" withText={true} />
            <div className="text-sm text-gray-500">Welcome Tour • Step 4 of 5</div>
          </div>
        </div>
      </div>

      {/* Progress Bar */}
      <div className="bg-white border-b">
        <div className="max-w-6xl mx-auto px-6 py-3">
          <div className="flex items-center">
            <div className="flex-1 bg-gray-200 rounded-full h-2">
              <div className="bg-blue-600 h-2 rounded-full" style={{ width: '80%' }}></div>
            </div>
            <span className="ml-3 text-sm text-gray-600">4 of 5</span>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-6xl mx-auto px-6 py-8">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-12 h-12 bg-blue-600 rounded-full mb-4">
            <PlayIcon className="w-6 h-6 text-white" />
          </div>
          
          <h1 className="text-3xl font-bold text-gray-900 mb-4">
            Hands-On Practice
          </h1>
          
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Time to practice! Complete these tasks with your real data to build 
            confidence using Sparvi in your daily workflow.
          </p>
        </div>

        {/* Progress Overview */}
        <div className="bg-white rounded-lg shadow-sm border p-6 mb-8">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold text-gray-900">Your Progress</h2>
            <div className="text-sm text-gray-500">
              {completedTasks.size} of {tasks.length} completed
            </div>
          </div>
          
          <div className="flex items-center mb-4">
            <div className="flex-1 bg-gray-200 rounded-full h-3">
              <div 
                className="bg-green-500 h-3 rounded-full transition-all duration-300" 
                style={{ width: `${completionRate}%` }}
              ></div>
            </div>
            <span className="ml-3 text-sm font-medium text-gray-700">
              {Math.round(completionRate)}%
            </span>
          </div>

          {completionRate >= 75 && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="flex items-center">
                <CheckCircleIcon className="w-5 h-5 text-green-600 mr-3" />
                <div>
                  <p className="font-medium text-green-900">Great progress!</p>
                  <p className="text-green-700 text-sm">You're ready to use Sparvi confidently. Complete the remaining tasks when you have time.</p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Task List */}
        <div className="space-y-6">
          {tasks.map((task, index) => {
            const IconComponent = task.icon;
            const isCompleted = completedTasks.has(task.id);
            
            return (
              <div 
                key={task.id}
                className={`bg-white rounded-lg shadow-sm border transition-all duration-200 ${
                  isCompleted ? 'border-green-200 bg-green-50' : 'hover:shadow-md'
                }`}
              >
                <div className="p-6">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start flex-1">
                      {/* Task Icon */}
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center mr-4 flex-shrink-0 ${
                        isCompleted ? 'bg-green-100' : 'bg-blue-100'
                      }`}>
                        {isCompleted ? (
                          <CheckCircleIcon className="w-5 h-5 text-green-600" />
                        ) : (
                          <IconComponent className="w-5 h-5 text-blue-600" />
                        )}
                      </div>

                      {/* Task Content */}
                      <div className="flex-1">
                        <div className="flex items-center mb-2">
                          <h3 className={`text-lg font-semibold mr-3 ${
                            isCompleted ? 'text-green-900' : 'text-gray-900'
                          }`}>
                            {task.title}
                          </h3>
                          
                          <div className="flex items-center space-x-2">
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${getDifficultyColor(task.difficulty)}`}>
                              {task.difficulty}
                            </span>
                            <div className="flex items-center text-xs text-gray-500">
                              <ClockIcon className="w-3 h-3 mr-1" />
                              {task.estimated}
                            </div>
                          </div>
                        </div>

                        <p className={`mb-4 ${isCompleted ? 'text-green-700' : 'text-gray-600'}`}>
                          {task.description}
                        </p>

                        {/* Task Steps */}
                        <div className="mb-4">
                          <h4 className="text-sm font-medium text-gray-900 mb-2">Steps:</h4>
                          <ol className="list-decimal list-inside space-y-1">
                            {task.steps.map((step, stepIndex) => (
                              <li key={stepIndex} className="text-sm text-gray-600">
                                {step}
                              </li>
                            ))}
                          </ol>
                        </div>
                      </div>
                    </div>

                    {/* Task Actions */}
                    <div className="flex flex-col items-end space-y-2 ml-4">
                      {!isCompleted ? (
                        <>
                          <Link
                            to={task.actionLink}
                            className="inline-flex items-center px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors"
                          >
                            <EyeIcon className="w-4 h-4 mr-2" />
                            {task.actionText}
                          </Link>
                          
                          <button
                            onClick={() => handleTaskComplete(task.id)}
                            className="text-sm text-gray-500 hover:text-gray-700"
                          >
                            Mark as completed
                          </button>
                        </>
                      ) : (
                        <div className="text-center">
                          <CheckCircleIcon className="w-8 h-8 text-green-600 mx-auto mb-1" />
                          <div className="text-sm font-medium text-green-900">Completed</div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Encouragement Message */}
        <div className="bg-white rounded-lg shadow-sm border p-6 mt-8">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Remember</h2>
          <div className="text-gray-600 space-y-2">
            <p>• Take your time with each task - this is about learning, not speed</p>
            <p>• Don't worry about breaking anything - you're exploring your own data</p>
            <p>• The tasks are designed to simulate real-world scenarios you'll encounter</p>
            <p>• You can always return to complete tasks later from the main application</p>
          </div>
        </div>

        {/* Navigation */}
        <div className="flex justify-between items-center mt-8">
          <button
            onClick={handleBack}
            className="inline-flex items-center px-4 py-2 text-gray-600 hover:text-gray-800 font-medium"
          >
            <ArrowLeftIcon className="mr-2 h-4 w-4" />
            Back
          </button>
          
          <button
            onClick={handleContinue}
            className="inline-flex items-center px-6 py-3 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
          >
            {completionRate >= 50 ? 'Finish Tour' : 'Continue Anyway'}
            <ArrowRightIcon className="ml-2 h-5 w-5" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default WelcomeTasksPage;